// CSV serialization helpers for the Manufacturing → Inventory mode.
//
// `EXAMPLE_BULK_IMPORT_CSV` is the starter file the bulk-import modal offers
// for download. `materialsToCsv` produces the inventory printout the
// Inventory panel offers as "Export CSV". Both go through `downloadCsv` which
// prepends a UTF-8 BOM so Excel opens non-ASCII names correctly.

import type { MfgMaterial } from "@/lib/manufacturing/types";

const INVENTORY_EXPORT_HEADERS = [
  "code",
  "name",
  "unit",
  "category",
  "shipping_unit",
  "shipping_unit_weight",
  "pieces_per_shipping_unit",
  "on_hand_qty",
  "unit_cost",
  "inventory_value",
  "is_active",
] as const;

const BULK_IMPORT_HEADERS = [
  "name",
  "category",
  "unit",
  "shipping_unit",
  "shipping_unit_weight",
  "pieces_per_shipping_unit",
  "code",
  "qty",
  "price",
  "vendor",
  "notes",
  "is_active",
] as const;

const NEEDS_QUOTING = /[",\r\n]/;

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  if (!NEEDS_QUOTING.test(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

function rowsToCsv(headers: readonly string[], rows: Array<Array<unknown>>): string {
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(row.map(escapeCsvCell).join(","));
  }
  return lines.join("\n") + "\n";
}

/**
 * Renders the current materials list as a CSV printout. Caller is expected
 * to have already filtered the list to whatever the user is looking at.
 */
export function materialsToCsv(materials: MfgMaterial[]): string {
  const rows = materials.map((m) => {
    const onHand = Number(m.on_hand_qty) || 0;
    const unitCost = Number(m.unit_cost) || 0;
    const inventoryValue = Number((onHand * unitCost).toFixed(2));
    return [
      m.code,
      m.name,
      m.unit,
      m.category,
      m.shipping_unit,
      m.shipping_unit_weight,
      Number(m.pieces_per_shipping_unit) > 0 ? m.pieces_per_shipping_unit : 1,
      onHand,
      unitCost,
      inventoryValue,
      m.is_active ? "true" : "false",
    ];
  });
  return rowsToCsv(INVENTORY_EXPORT_HEADERS, rows);
}

/**
 * Starter CSV the bulk-import modal offers for download. The headers cover
 * every recognized key (required + optional) so users can edit the columns
 * they care about and leave the rest blank. Schema enforcement: only `name`
 * and `category` are required; everything else has a default or is optional.
 */
export const EXAMPLE_BULK_IMPORT_CSV = rowsToCsv(BULK_IMPORT_HEADERS, [
  [
    'ProStruct 3/4" Flooring',
    "FLOOR",
    "sheet",
    "bundle",
    47.5,
    50,
    "",
    623,
    34.97,
    "ABC Lumber",
    "",
    "true",
  ],
  ['TechShield OSB 7/16"', "FLOOR", "sheet", "bundle", 38.0, 50, "", 1082, 17.53, "", "", "true"],
  ['OSB 7/16"', "FLOOR", "sheet", "bundle", 38.0, 50, "", 780, 9.27, "", "", "true"],
]);

/**
 * Triggers a browser download for `csvText` under `filename`. Adds a UTF-8
 * BOM so Excel doesn't mangle non-ASCII characters. No-op when `window` is
 * undefined (SSR / test).
 */
export function downloadCsv(filename: string, csvText: string): void {
  if (typeof window === "undefined") return;
  const blob = new Blob(["\uFEFF" + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
